import { Controller } from "@hotwired/stimulus";
import TomSelect from "tom-select";
import { updateSelect } from "shared/selects/select_helpers";
import _, { isEmpty } from "lodash";
import { getManufacturersForSelect } from "shared/device_types/device_types";
import { DO_NOT_EMIT_EVENTS } from "constants/select_constants";
import { deviceTypeStore } from "../../../stores/device_types/device_type_store";
import { manufacturerStore } from "../../../stores/device_types/manufacturer_store";
import { useStore } from "stimulus-store";

// Connects to data-controller="selects--device-types--manufacturer-select"
export default class extends Controller {
    _deviceTypeId

    static stores = [
        deviceTypeStore,
        manufacturerStore
    ]

    static values = {
        parentDeviceType: String,
        selected: String
    }

    connect() {
        useStore(this);
    }


    initialize() {
        const manufacturerSelectElement = this.element;
        this._deviceTypeId = this.parentDeviceTypeValue;
        const manufacturerId = this.selectedValue;

        // Require a device type to fetch manufacturers
        if (!isEmpty(this._deviceTypeId)) {
            // Fetch this device type's manufacturers
            getManufacturersForSelect(this._deviceTypeId).then((results) => {
                const control = new TomSelect(manufacturerSelectElement, {
                    placeholder: 'Select manufacturer',
                    create: false,
                    allowClear: true,
                    valueField: 'id',
                    labelField: 'text',
                    searchField: 'text',
                    sortField: {
                        field: "text",
                        direction: "asc"
                    },
                    // options: [],
                    // options: results,
                    maxOptions: null, // tom-select will truncate the select to 50 options by default
                    plugins: ['remove_button'], // This enables a "clear" button on selected items
                    onChange: (event) => this._manufacturerSelectionChanged(event), // Using an arrow function here allows us to reference methods in the class using this
                    onDelete: (values) => this._manufacturerUnselectedEvent(values),
                    closeAfterSelect: true // This closes the select after a selection is made.
                });

                // Add the expected options to the select. We're doing it this way instead of
                // in the constructor so the items appear in the underlying select. This makes
                // it possible to test using capybara matchers
                updateSelect(this.element.id, results);

                // Syncing will ensure options are stubbed out within the select element. This
                // ensures the tests will have the data in the DOM it can test against
                control.sync();

                if (!_.isEmpty(manufacturerId)) {
                    // Setting silent to true suppresses change events
                    // https://tom-select.js.org/docs/api/
                    control.setValue(manufacturerId, DO_NOT_EMIT_EVENTS);

                    // Set the manufacturer store value
                    this.setManufacturerStoreValue({ id: manufacturerId });
                }
            })
        } else {
            const control = new TomSelect(manufacturerSelectElement, {
                placeholder: 'Select manufacturer',
                create: false,
                allowClear: true,
                valueField: 'id',
                labelField: 'text',
                searchField: 'text',
                sortField: {
                    field: "text",
                    direction: "asc"
                },
                maxOptions: null, // tom-select will truncate the select to 50 options by default
                plugins: ['remove_button'], // This enables a "clear" button on selected items
                onChange: (event) => this._manufacturerSelectionChanged(event), // Using an arrow function here allows us to reference methods in the class using this
                onDelete: (values) => this._manufacturerUnselectedEvent(values),
                closeAfterSelect: true // This closes the select after a selection is made.
            });

            // Disable the manufacturer select since no device type is selected yet.
            // See handleDeviceTypeChanged for where this re-enables (only once its
            // options are actually populated -- same pattern as the locale selects).
            this.element.disabled = true;
            if (this.element.tomselect) {
                this.element.tomselect.disable();
            }
        }
    }

    _manufacturerSelectionChanged(event) {
        // Simply return we don't have an ID (so no selection was made). This happens
        // during initialization when options are loaded...
        if (isEmpty(event)) {
            return;
        }

        // Send an event telling the name input, parts select, and model select
        // controllers to enable those elements. I stole this method from here:
        // https://lmika.org/2021/02/17/communication-among-stimulus.html
        window.dispatchEvent(new CustomEvent('manufacturerSelectionMade', {
            detail: {
                device_type: this._deviceTypeId,
                manufacturer: event
            },
            bubbles: true // This needs to be explicitly set to enable event bubbling
        }));

        // Set the manufacturer store value
        this.setManufacturerStoreValue({ id: event });
    }

    _manufacturerUnselectedEvent(values) {
        // If we don't have an ID, clear out the current selections and return
        window.dispatchEvent(new Event('manufacturerSelectionCleared', {
            detail: {},
            bubbles: true // This needs to be explicitly set to enable event bubbling
        }));

        // Set the manufacturer store value
        this.setManufacturerStoreValue({});
    }

    handleDeviceTypeChanged(event) {
        // Return if this event is empty. This may happen during initialization.
        if (event === undefined) {
            return;
        }

        // Get the new selected device type ID from the event
        this._deviceTypeId = event.detail.device_type;

        // If this ID is not empty, grab its manufacturers and update this select
        if (!_.isEmpty(this._deviceTypeId) && !_.isEmpty(event.detail.device_type)) {
            getManufacturersForSelect(this._deviceTypeId).then((results) => {
                updateSelect(this.element.id, results);

                // Enable the manufacturer select only once its options are actually
                // populated. Enabling before this resolved would let a user or
                // automated test open a still-empty dropdown.
                this.element.disabled = false;
                if (this.element.tomselect) {
                    this.element.tomselect.enable();
                }
            });
        }
    }

    onDeviceTypeStoreChanged(deviceType) {
        if (deviceType && deviceType.id) {
            // TODO: REMOVE OUR DEPENDENCY ON THIS CLASS VARIABLE
            // Get the new selected device type ID from the event
            this._deviceTypeId = deviceType.id

            getManufacturersForSelect(deviceType.id).then((results) => {
                updateSelect(this.element.id, results);

                // Enable the manufacturer select only once its options are actually
                // populated. See handleDeviceTypeChanged for the full rationale.
                this.element.disabled = false;
                if (this.element.tomselect) {
                    this.element.tomselect.enable();
                }
            });
        }
    }

    handleDeviceTypeCleared(event) {
        // Clear the variable that stores the device type id
        this._deviceTypeId = null;

        // Clear the manufacturers
        updateSelect(this.element.id, []);

        // Disable the manufacturer select since no device type is selected anymore
        this.element.disabled = true;
        if (this.element.tomselect) {
            this.element.tomselect.disable();
        }
    }
}